import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { configureCardGridClassName } from 'constant/configure-layout';
import { cn, formatDate } from 'lib/utils';
import { AI_CREDITS_METER } from 'features/credits/constants/credits';
import { SeatsInUse } from 'features/subscription/components/seats-in-use';
import { BillingCycleToggle } from 'features/subscription/components/subscription-plan';
import { uniquePlanIntervals } from 'features/subscription/utils/billing-cycle';
import {
  defaultSeatInterval,
  PlanSeats,
  seatPriceLabel,
  withInterval,
} from 'features/subscription/utils/seat-pools';
import { BuySeatsDialog } from './buy-seats-dialog';
import { PlanDetailsDialog } from './plan-details-dialog';
import { PlanActions, planStateLabel, planUnavailableNote, seatsLabel } from './plan-actions';

interface SeatsOverviewProps {
  planSeats: PlanSeats[];
  title?: string;
  description?: string;
  className?: string;
}

/** One card per plan: seats in use and available. Plans not bought yet show 0 and can be bought here. */
export const SeatsOverview = ({ planSeats, title, description, className }: SeatsOverviewProps) => {
  const { t } = useTranslation();
  const [buying, setBuying] = useState<{ focusCode?: string; focusInterval?: string } | null>(null);
  const [viewing, setViewing] = useState<PlanSeats | null>(null);
  const [cycles, setCycles] = useState<Record<string, string>>({});

  if (planSeats.length === 0) return null;

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-base font-medium text-foreground">{title ?? t('SEATS', 'Seats')}</h2>
          <p className="text-sm text-muted-foreground">
            {description ??
              t('SEATS_DESCRIPTION', 'Seats are bought per plan. Give people a plan from the Plan column; a missing seat can be bought there.')}
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setBuying({})}>
          <Plus className="mr-1 h-4 w-4" />
          {t('BUY_SEATS', 'Buy seats')}
        </Button>
      </div>

      {/* Same grid as agent cards; rows grow because seat cards can hold a cycle toggle and notes. */}
      <div className={cn(configureCardGridClassName, 'auto-rows-auto')}>
        {planSeats.map((entry) => {
          const interval = cycles[entry.code] ?? defaultSeatInterval(entry);
          const priced = withInterval(entry, interval);
          const cyclesForPlan = uniquePlanIntervals(entry.plan?.prices);
          const price = seatPriceLabel(t, priced, true);
          const note = planUnavailableNote(t, entry);
          const monthlyCredits = entry.plan?.meters.find((meter) => meter.meterKey === AI_CREDITS_METER)?.includedQuantity;

          return (
            <Card key={entry.code} className="min-w-0 rounded-section border-border/70 shadow-none">
              <CardContent className="flex h-full flex-col gap-4 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-medium text-foreground">{entry.name}</h3>
                    {price && <p className="mt-1 text-sm text-muted-foreground">{price}</p>}
                  </div>
                  <span
                    className={cn(
                      'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs text-muted-foreground',
                      entry.awaitingPayment && 'border-warning text-warning-high-emphasis',
                      !entry.pool && 'border-dashed'
                    )}
                  >
                    {planStateLabel(t, entry)}
                  </span>
                </div>

                {/* A plan already bought stays on its own cycle, so only a first buy can pick one. */}
                {!entry.pool && cyclesForPlan.length > 1 && (
                  <div className="self-start">
                    <BillingCycleToggle
                      compact
                      intervals={cyclesForPlan}
                      selectedInterval={interval}
                      onChange={(interval) => setCycles((current) => ({ ...current, [entry.code]: interval }))}
                    />
                  </div>
                )}

                {monthlyCredits != null && (
                  <div className="flex items-center justify-between gap-3 border-t border-border pt-4 text-sm">
                    <span className="text-muted-foreground">{t('CREDITS_A_MONTH', 'Credits a month')}</span>
                    <span className="text-foreground">{monthlyCredits.toLocaleString()}</span>
                  </div>
                )}

                {!entry.pool && <p className="text-sm text-muted-foreground">{t('NO_SEATS_BOUGHT', 'No seats bought yet')}</p>}
                {entry.awaitingPayment && <p className="text-sm text-muted-foreground">{seatsLabel(t, entry.purchased)}</p>}
                {entry.pool && !entry.awaitingPayment && (
                  <SeatsInUse held={entry.held} total={entry.held + entry.freeThisPeriod} />
                )}

                {note && <p className="text-xs text-muted-foreground">{note}</p>}
                {entry.pool && !entry.awaitingPayment && entry.periodEndUtc && (
                  <p className="text-xs text-muted-foreground">
                    {t('SEATS_PERIOD', {
                      start: formatDate(entry.periodStartUtc),
                      end: formatDate(entry.periodEndUtc),
                      defaultValue: '{{start}} to {{end}}',
                    })}
                    {' · '}
                    {entry.cancelScheduled
                      ? t('PLAN_DOES_NOT_RENEW', 'does not renew')
                      : t('RENEWS_WITH', {
                          seats: seatsLabel(t, entry.seatsNextPeriod),
                          defaultValue: 'renews with {{seats}}',
                        })}
                  </p>
                )}

                <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {entry.canAddSeats && !entry.cancelScheduled && (
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => setBuying({ focusCode: entry.code, focusInterval: interval })}
                      >
                        {entry.pool ? t('MANAGE_SEATS', 'Manage seats') : t('BUY_SEATS', 'Buy seats')}
                      </Button>
                    )}
                    {entry.plan && !entry.awaitingPayment && (
                      <Button variant="ghost" className="rounded-full" onClick={() => setViewing(priced)}>
                        {t('PLAN_DETAILS', 'Details')}
                      </Button>
                    )}
                  </div>
                  <PlanActions entry={entry} checkoutOnly />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <PlanDetailsDialog entry={viewing} onClose={() => setViewing(null)} />

      <BuySeatsDialog
        open={!!buying}
        // A plan card opens on its own plan; every plan stays in the list.
        planSeats={planSeats}
        focusCode={buying?.focusCode}
        focusInterval={buying?.focusInterval}
        onClose={() => setBuying(null)}
      />
    </section>
  );
};
